import { FC, ReactNode, useState } from "react";
import { CenteredCard } from "../../containers/card";
import { Box, Button, Spinner, TextField, Typography } from "@kamstrup/kfl";
import { commonStyles } from "../../styles/commonStyles";
import { SubmitEmailDto } from "../../models/signupEmailDto";
import { getLan } from "../../utils/languageManager";
import { useWorkflowIdWithNotification } from "../../hooks/useGetWorkflowId/useWorkflowIdWithNotification";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { navigateSecurely } from "../../utils/navigation/navigationUtils";

interface SendEmailStepProps {
  title: string;
  description: string;
  infoBox: ReactNode;
  showReturnToLogin?: boolean;
  onSubmit: (dto: SubmitEmailDto) => void;
  loading?: boolean;
  error?: string | null;
}

export const SendEmailStep: FC<SendEmailStepProps> = ({
  title,
  description,
  infoBox,
  showReturnToLogin = false,
  onSubmit,
  loading = false
}) => {
  const { translateString } = useTranslation();
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getWorkflowIdSafely } = useWorkflowIdWithNotification();

  const isEmailValid = (email: string): [boolean, string] => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (email.length > 64) {
      return [false, translateString("email_too_long")];
    }

    const [local, domain] = email.split("@");
    if (!local || !domain) {
      return [false, translateString("email_missing_parts")];
    }

    if (!emailRegex.test(email)) {
      return [false, translateString("email_invalid_format")];
    }

    return [true, ""];
  };

  const [isValid] = isEmailValid(email);

  const handleSubmit = async () => {
    if (loading || submitting) return;

    if (!isValid) {
      setEmailError(translateString("valid_email_error"));
      return;
    }

    setSubmitting(true);
    setEmailError(null);

    try {
      const workflowId = await getWorkflowIdSafely();
      if (!workflowId) {
        setEmailError(translateString("workflow_missing_error"));
        return;
      }

      const signupDto: SubmitEmailDto = {
        email,
        workflowId,
        language: getLan()
      };

      await onSubmit(signupDto);
    } catch (e) {
      setEmailError(translateString("error_sending_email"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnToLogin = () => {
    navigateSecurely();
  };

  return (
    <CenteredCard title={title}>
      <Typography variant="body4" sx={commonStyles.introText}>
        {description}
      </Typography>

      <Box sx={commonStyles.textFieldContainer}>
        <TextField
          size="large"
          label={translateString("email")}
          placeholder={translateString("example_email")}
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit();
            }
          }}
          error={!!emailError}
          helperText={emailError || ""}
          autoFocus
        />
      </Box>

      <Box>{infoBox}</Box>

      <Box sx={commonStyles.buttonContainer}>
        <Button
          startIcon={loading || submitting ? <Spinner /> : undefined}
          color="primary"
          size="medium"
          variant="contained"
          fullWidth
          sx={commonStyles.button}
          onClick={handleSubmit}
          disabled={loading || submitting || !email || !isValid}
        >
          {submitting
            ? translateString("loading")
            : loading
              ? translateString("sending")
              : translateString("send_verification_code")}
        </Button>
      </Box>

      {showReturnToLogin && (
        <>
          <Box sx={commonStyles.divider} />
          <Box sx={commonStyles.buttonContainer}>
            <Button
              color="primary"
              size="medium"
              variant="text"
              fullWidth
              sx={commonStyles.button}
              onClick={handleReturnToLogin}
              disabled={loading || submitting}
            >
              <Typography variant="body4">{translateString("return_to_login")}</Typography>
            </Button>
          </Box>
        </>
      )}
    </CenteredCard>
  );
};
